import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { appMainService } from "@/ipc";
import { formatError } from "@/utils/errors";
import { isValidServerURL } from "@/utils/url-validation";
import { useCallback, useEffect, useState, type FC } from "react";

export const DesktopSignin: FC = () => {
    const { toast } = useToast();
    const cpHost = window.location.origin;

    const [isEditingServer, setIsEditingServer] = useState(false);
    const [serverUrl, setServerUrl] = useState("");
    const [isCustomDomain, setIsCustomDomain] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Load current custom domain config on mount
    useEffect(() => {
        const loadConfig = async () => {
            if (!appMainService) return;
            try {
                const config = await appMainService.getCustomDomainConfig();
                setServerUrl(config.customDomain || "");
                setIsCustomDomain(!!config.customDomain);
            } catch (error) {
                console.error("Failed to load custom domain config:", error);
            }
        };
        void loadConfig();
    }, []);

    const handleConnectToGitpod = useCallback(async () => {
        const result = await appMainService!.signin({ host: cpHost });
        if (result === "timeout") {
            toast({ title: "Timed out", description: "Please try again" });
            return;
        }
        const parsed = parseSigninResult(result);
        if (!parsed) {
            toast({ title: "Unexpected parse error", description: "Please try again" });
            return;
        }
        try {
            // clear cookies first
            await fetch("/auth/oidc/logout", {
                method: "POST",
                credentials: "same-origin",
            });
        } catch {
            // explicitly ignore as we're about to login using the desktop flow
        }
        try {
            const response = await fetch("/auth/desktop/callback", {
                method: "POST",
                credentials: "same-origin",
                body: new URLSearchParams(parsed),
            });
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            window.location.href = "/";
        } catch (error) {
            toast({ title: "Unexpected parse error", description: formatError(error) });
        }
    }, [cpHost, toast]);

    const handleSaveServer = useCallback(async () => {
        if (!appMainService) return;

        let normalizedUrl: string | undefined;
        const trimmedUrl = serverUrl.trim();

        // Validate and normalize URL if provided
        if (trimmedUrl) {
            if (!isValidServerURL(trimmedUrl)) {
                toast({
                    title: "Invalid URL",
                    description: "Please enter a valid HTTPS URL (e.g., https://custom.example.com). HTTP is only allowed for localhost.",
                });
                return;
            }
            try {
                const parsed = new URL(trimmedUrl);
                // Normalize: use origin to strip trailing slashes and paths
                normalizedUrl = parsed.origin;
            } catch {
                toast({
                    title: "Invalid URL",
                    description: "Please enter a valid URL (e.g., https://custom.example.com)",
                });
                return;
            }
        }

        setIsSaving(true);
        try {
            const { success } = await appMainService.setCustomDomain({ domain: normalizedUrl });
            if (!success) {
                setIsSaving(false);
                toast({ title: "Failed to save", description: "Could not persist the server URL. Please try again." });
                return;
            }
            // Restart the app to apply the new domain
            await appMainService.restartApp();
        } catch (error) {
            setIsSaving(false);
            toast({ title: "Failed to save", description: formatError(error) });
        }
    }, [serverUrl, toast]);

    const handleResetToDefault = useCallback(async () => {
        if (!appMainService) return;

        setIsSaving(true);
        try {
            const { success } = await appMainService.setCustomDomain({ domain: undefined });
            if (!success) {
                setIsSaving(false);
                toast({ title: "Failed to reset", description: "Could not clear the server URL. Please try again." });
                return;
            }
            // Restart the app to apply the change
            await appMainService.restartApp();
        } catch (error) {
            setIsSaving(false);
            toast({ title: "Failed to reset", description: formatError(error) });
        }
    }, [toast]);

    return (
        <div id="desktop-signin-container" className="flex flex-col gap-2">
            <div
                className="flex justify-center"
                onClick={handleConnectToGitpod}
                data-tracking-id="connect-to-ona-desktop-signin"
            >
                <Button id="signin" variant={"primary"} size={"lg"}>
                    Sign into Ona
                </Button>
            </div>
            <Popover open={isEditingServer} onOpenChange={setIsEditingServer}>
                <PopoverTrigger asChild>
					<Button variant="ghost" className="text-sm text-content-muted" data-tracking-id="edit-server-url">
                        <span>Sign into https://app.gitpod.io</span>
                        <IconChevronDownSmall size="sm" className="transition-transform group-hover:translate-y-0.5" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="center">
                    <div className="flex flex-col gap-3">
                        <div>
                            <Text className="text-sm font-medium text-content-primary">Ona Custom Domain URL</Text>
                            <Text className="mt-1 text-xs text-content-tertiary">
                                Connect to a custom management plane domain name. Leave empty for default.
                            </Text>
                        </div>
                        <Input
                            type="url"
                            placeholder="https://app.gitpod.io"
                            value={serverUrl}
                            onChange={(e) => setServerUrl(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    void handleSaveServer();
                                }
                                if (e.key === "Escape") {
                                    setIsEditingServer(false);
                                }
                            }}
                            disabled={isSaving}
                            autoFocus
                        />
                        <div className="flex justify-between gap-2">
                            {isCustomDomain && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleResetToDefault}
                                    disabled={isSaving}
                                    data-tracking-id="reset-server-to-default"
                                >
                                    Reset to Default
                                </Button>
                            )}
                            <div className="ml-auto flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setIsEditingServer(false)}
                                    disabled={isSaving}
                                    data-tracking-id="cancel-server-edit"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleSaveServer}
                                    disabled={isSaving}
                                    loading={isSaving}
                                    data-tracking-id="save-server-url"
                                >
                                    Save & Restart
                                </Button>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

function parseSigninResult(result: string) {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const asObject = JSON.parse(result);
        if (typeof asObject === "object" && "code" in asObject && "code_verifier" in asObject) {
            return {
                code: asObject.code as string,
                code_verifier: asObject.code_verifier as string,
            };
        }
    } catch (error) {
        console.error("error processing signin result", error);
    }
}
